import React, { useState } from "react";
import { Button, Modal, Form, Row, Col, ListGroup } from "react-bootstrap";
import { FaCalendarAlt } from "react-icons/fa";
import { format, subDays, subMonths, subYears, startOfMonth, endOfMonth, parseISO } from "date-fns";
import th from "date-fns/locale/th";

export default function DateRangeSelector({ dateRange, onChange }) {
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState("quick"); // quick, month, custom
  
  // Custom states for modal
  const [customStart, setCustomStart] = useState(dateRange.startDate);
  const [customEnd, setCustomEnd] = useState(dateRange.endDate);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'MM'));
  const [selectedYear, setSelectedYear] = useState(format(new Date(), 'yyyy'));

  const handleClose = () => setShow(false);
  const handleShow = () => {
    setCustomStart(dateRange.startDate);
    setCustomEnd(dateRange.endDate);
    setShow(true);
  };

  const applyRange = (start, end) => {
    onChange({ startDate: start, endDate: end });
    handleClose();
  };

  const handleQuickSelect = (days, months, years) => {
    const end = new Date();
    let start = new Date();
    if (days) start = subDays(start, days);
    if (months) start = subMonths(start, months);
    if (years) start = subYears(start, years);
    
    applyRange(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'));
  };

  const handleMonthSelect = () => {
    if (!selectedMonth || !selectedYear) return;
    const date = parseISO(`${selectedYear}-${selectedMonth}-01`);
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    applyRange(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'));
  };

  const handleCustomSelect = () => {
    applyRange(customStart, customEnd);
  };

  // Display text on the button
  const displayStart = format(parseISO(dateRange.startDate), 'dd/MM/yyyy');
  const displayEnd = format(parseISO(dateRange.endDate), 'dd/MM/yyyy');

  return (
    <>
      <Button 
        variant="outline-secondary" 
        className="d-flex align-items-center bg-white" 
        style={{ borderRadius: '8px', border: '1px solid #ced4da', fontWeight: '500', color: '#495057' }}
        onClick={handleShow}
      >
        <FaCalendarAlt className="me-2 text-primary" />
        {displayStart} - {displayEnd}
      </Button>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h5 mb-0 fw-bold text-primary">เลือกช่วงเวลารายงาน</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="d-flex align-items-stretch" style={{ minHeight: '280px' }}>
            {/* Sidebar for modes */}
            <div className="bg-light border-end" style={{ width: '140px', minHeight: '280px', borderBottomLeftRadius: 'calc(var(--bs-modal-border-radius, 0.5rem) - 1px)' }}>
              <ListGroup variant="flush">
                <ListGroup.Item 
                  action 
                  active={mode === 'quick'} 
                  onClick={() => setMode('quick')}
                  className="border-0 rounded-0"
                  style={{ cursor: 'pointer', fontWeight: mode === 'quick' ? 'bold' : 'normal' }}
                >
                  ย้อนหลัง
                </ListGroup.Item>
                <ListGroup.Item 
                  action 
                  active={mode === 'month'} 
                  onClick={() => setMode('month')}
                  className="border-0"
                  style={{ cursor: 'pointer', fontWeight: mode === 'month' ? 'bold' : 'normal' }}
                >
                  เลือกเดือน
                </ListGroup.Item>
                <ListGroup.Item 
                  action 
                  active={mode === 'custom'} 
                  onClick={() => setMode('custom')}
                  className="border-0"
                  style={{ cursor: 'pointer', fontWeight: mode === 'custom' ? 'bold' : 'normal' }}
                >
                  กำหนดเอง
                </ListGroup.Item>
              </ListGroup>
            </div>

            {/* Content area */}
            <div className="p-3 flex-grow-1 d-flex flex-column">
              {mode === 'quick' && (
                <div>
                  <Form.Label className="text-muted fw-bold small text-uppercase mb-3">เลือกช่วงเวลาย้อนหลัง</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    <Button variant="outline-primary" size="sm" className="px-3" onClick={() => handleQuickSelect(7, 0, 0)}>7 วัน</Button>
                    <Button variant="outline-primary" size="sm" className="px-3" onClick={() => handleQuickSelect(0, 1, 0)}>1 เดือน</Button>
                    <Button variant="outline-primary" size="sm" className="px-3" onClick={() => handleQuickSelect(0, 3, 0)}>3 เดือน</Button>
                    <Button variant="outline-primary" size="sm" className="px-3" onClick={() => handleQuickSelect(0, 6, 0)}>6 เดือน</Button>
                    <Button variant="outline-primary" size="sm" className="px-3" onClick={() => handleQuickSelect(0, 0, 1)}>1 ปี</Button>
                  </div>
                </div>
              )}

              {mode === 'month' && (
                <div>
                  <Form.Label className="text-muted fw-bold small text-uppercase mb-3">เลือกเดือนและปี</Form.Label>
                  <Row className="mb-3 g-2">
                    <Col xs={7}>
                      <Form.Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                        <option value="01">มกราคม (January)</option>
                        <option value="02">กุมภาพันธ์ (February)</option>
                        <option value="03">มีนาคม (March)</option>
                        <option value="04">เมษายน (April)</option>
                        <option value="05">พฤษภาคม (May)</option>
                        <option value="06">มิถุนายน (June)</option>
                        <option value="07">กรกฎาคม (July)</option>
                        <option value="08">สิงหาคม (August)</option>
                        <option value="09">กันยายน (September)</option>
                        <option value="10">ตุลาคม (October)</option>
                        <option value="11">พฤศจิกายน (November)</option>
                        <option value="12">ธันวาคม (December)</option>
                      </Form.Select>
                    </Col>
                    <Col xs={5}>
                      <Form.Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                        {Array.from({ length: Math.max(1, new Date().getFullYear() - 2026 + 1) }, (_, i) => new Date().getFullYear() - i).map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Row>
                  <Button variant="primary" className="w-100" onClick={handleMonthSelect}>ตกลง</Button>
                </div>
              )}

              {mode === 'custom' && (
                <div>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted fw-bold small text-uppercase">วันที่เริ่มต้น (Start Date)</Form.Label>
                    <Form.Control 
                      type="date" 
                      value={customStart} 
                      onChange={(e) => setCustomStart(e.target.value)} 
                    />
                  </Form.Group>
                  <Form.Group className="mb-4">
                    <Form.Label className="text-muted fw-bold small text-uppercase">วันที่สิ้นสุด (End Date)</Form.Label>
                    <Form.Control 
                      type="date" 
                      value={customEnd} 
                      onChange={(e) => setCustomEnd(e.target.value)} 
                    />
                  </Form.Group>
                  <Button variant="primary" className="w-100" onClick={handleCustomSelect}>ตกลง</Button>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
